import React, { useState, useMemo, useEffect } from 'react';
import { formatCurrency } from '../utils/currency';

const btnPrimary = 'inline-flex items-center justify-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed';
const btnSecondary = 'inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:opacity-50 disabled:cursor-not-allowed';
const inputClass = 'block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6';

const PAYMENT_TYPES = [
  { value: 'PAYMENT_CASH', label: 'Cash' },
  { value: 'PAYMENT_BANK_TRANSFER', label: 'Bank Transfer' },
  { value: 'PAYMENT_TRUST_ACCOUNT', label: 'Trust Account' },
  { value: 'PAYMENT_CREDIT_CARD', label: 'Credit Card' },
  { value: 'PAYMENT_CHEQUE', label: 'Cheque' },
  { value: 'PAYMENT_OTHER', label: 'Other' },
];

export default function InvoiceJournalModal({ open, onClose, onSaved, invoiceId, authAxios, mode = 'payment', maxAmount }) {
  const isPayment = mode === 'payment';
  const isWriteOff = mode === 'write_off';
  const isCredit = mode === 'credit';
  const isRefund = mode === 'refund';
  const initialType = isPayment
    ? PAYMENT_TYPES[0].value
    : isWriteOff
      ? 'WRITE_OFF'
      : isCredit
        ? 'CREDIT_NOTE'
        : 'REFUND';
  const [journalType, setJournalType] = useState(initialType);
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [hasTrustAccount, setHasTrustAccount] = useState(false);
  const numericAmount = useMemo(() => Number(amount || 0), [amount]);
  const requiresCashReason = useMemo(
    () => isPayment && journalType === 'PAYMENT_CASH' && numericAmount >= 10000,
    [isPayment, journalType, numericAmount]
  );
  const enforceMax = !isPayment && maxAmount != null;
  const exceedsMax = useMemo(() => enforceMax && numericAmount > Number(maxAmount), [numericAmount, maxAmount, enforceMax]);

  useEffect(() => {
    let mounted = true;
    const checkTrust = async () => {
      if (!open || !invoiceId) return;
      try {
        const invResp = await authAxios.get(`/invoices/${invoiceId}/`);
        const clientId = invResp.data.client;
        if (!clientId) {
          if (mounted) setHasTrustAccount(false);
          return;
        }
        // Fetch trust accounts; backend may support client filter, otherwise filter client-side
        const taResp = await authAxios.get('/trust-accounts/');
        const tas = Array.isArray(taResp.data) ? taResp.data : (taResp.data.results || []);
        const found = tas.some(t => t.client === clientId && t.is_active);
        if (mounted) setHasTrustAccount(found);
      } catch (e) {
        if (mounted) setHasTrustAccount(false);
      }
    };
    checkTrust();
    return () => { mounted = false; };
  }, [open, invoiceId, authAxios]);

  if (!open) return null;

  const handleSave = async () => {
    if (requiresCashReason && !String(notes || '').trim()) {
      setError('Reason is required for cash transactions of P10,000 and above.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        invoice: invoiceId,
        journal_type: journalType,
        amount,
        notes,
      };
      const resp = await authAxios.post('invoice-journals/', payload);
      onSaved && onSaved(resp.data);
      onClose && onClose();
      // reset
      setAmount('');
      setNotes('');
      setJournalType(isPayment ? PAYMENT_TYPES[0].value : 'WRITE_OFF');
    } catch (e) {
      const data = e.response?.data;
      if (data?.detail) {
        setError(data.detail);
      } else if (data && typeof data === 'object') {
        // DRF field errors: { field: [msg] }
        const firstKey = Object.keys(data)[0];
        const firstVal = firstKey ? data[firstKey] : null;
        const msg = Array.isArray(firstVal) ? firstVal[0] : (firstVal || 'Failed to save');
        setError(String(msg));
      } else {
        setError('Failed to save');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">
            {isPayment && 'Record Payment'}
            {isWriteOff && 'Write Off'}
            {isCredit && 'Issue Credit Note'}
            {isRefund && 'Refund'}
          </h3>
          <button className="text-gray-500 hover:text-gray-700" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="mt-4 space-y-4">
          {isPayment ? (
            <div>
              <label className="block text-sm font-medium text-gray-900">Payment Method</label>
              <select className={`${inputClass} mt-1`} value={journalType} onChange={(e) => setJournalType(e.target.value)}>
                {PAYMENT_TYPES.filter(pt => pt.value !== 'PAYMENT_TRUST_ACCOUNT' || hasTrustAccount)
                  .map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-900">Type</label>
              <input className={`${inputClass} mt-1`} value={isWriteOff ? 'Write-Off' : isCredit ? 'Credit Note' : 'Refund'} readOnly />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-900">Amount</label>
            <input
              className={`${inputClass} mt-1 ${exceedsMax ? 'ring-red-500 focus:ring-red-600' : ''}`}
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              aria-invalid={exceedsMax ? 'true' : 'false'}
            />
            {enforceMax && (
              <p className="mt-1 text-xs text-gray-500">
                {isRefund ? 'Available to refund: ' : 'Remaining balance: '}{formatCurrency(maxAmount)}
              </p>
            )}
            {exceedsMax && (
              <p className="mt-1 text-xs text-red-600">
                {isRefund ? 'Amount exceeds refundable amount.' : 'Amount exceeds remaining balance.'}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900">
              {isPayment ? (requiresCashReason ? 'Reason (required)' : 'Notes (optional)') : 'Notes'}
            </label>
            <textarea
              className={`${inputClass} mt-1`}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                isPayment
                  ? (requiresCashReason ? 'Enter reason for this cash transaction (P10,000+)' : 'Optional note about this payment')
                  : isWriteOff
                    ? 'Reason for write-off'
                    : isCredit
                      ? 'Credit note reason/details'
                      : 'Refund reason/details'
              }
            />
            {requiresCashReason && !String(notes || '').trim() && (
              <p className="mt-1 text-xs text-red-600">Reason is required for cash transactions of P10,000 and above.</p>
            )}
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button className={btnSecondary} onClick={onClose}>Cancel</button>
          <button
            className={btnPrimary}
            onClick={handleSave}
            disabled={saving || !amount || exceedsMax || (requiresCashReason && !String(notes || '').trim())}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
